import {
  Autocomplete,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { ReactNode, useMemo, useState } from 'react'
import { EstadoDeLista } from './EstadoDeLista'

/** Opção de um filtro de seleção — vem dos valores presentes nas linhas, nunca de uma lista fixa. */
export interface OpcaoFiltro {
  valor: string
  rotulo: string
  /**
   * Agrupa as opções no menu. Dentro do mesmo grupo as escolhas combinam com OU; entre grupos
   * diferentes, com E (ex.: "Curadoria: Elegível" E "Reserva: Enviada").
   */
  grupo?: string
}

/**
 * Filtro opcional de uma coluna (opt-in: coluna sem `filtro` não ganha controle nenhum, e tabela
 * sem nenhuma coluna filtrável renderiza exatamente como antes). Os filtros só ESCOLHEM quais
 * linhas já carregadas aparecem — não calculam nada.
 *  - `texto`: "contém", sem acento/caixa; consulta só de dígitos e pontuação (CNPJ com ou sem
 *    máscara) também casa pelos dígitos;
 *  - `selecao`: múltipla, opções = valores presentes nas linhas;
 *  - `faixaNumero`: mínimo/máximo inclusivos (linha sem valor sai quando há limite);
 *  - `faixaData`: de/até inclusivos sobre ISO `YYYY-MM-DD` (linha sem data sai quando há limite).
 */
export type FiltroColuna<T> =
  | { tipo: 'texto'; textos: (linha: T) => Array<string | null | undefined>; placeholder?: string }
  | { tipo: 'selecao'; opcoes: (linha: T) => OpcaoFiltro[] }
  | { tipo: 'faixaNumero'; valor: (linha: T) => number | null | undefined }
  | { tipo: 'faixaData'; valor: (linha: T) => string | null | undefined }

/** Faixa crua dos dois campos (strings como digitadas). */
export interface FaixaFiltro {
  de: string
  ate: string
}
/** Valor de um filtro: texto, valores escolhidos ou faixa. */
export type ValorFiltro = string | string[] | FaixaFiltro
/** Estado dos filtros, por chave de coluna (`chave` ou, sem ela, `titulo`). */
export type FiltrosTabela = Record<string, ValorFiltro>

export interface ColunaTabelaAcao<T> {
  titulo: string
  /** Conteúdo da célula para a linha. */
  render: (linha: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  largura?: number | string
  /** Chave do filtro no estado; padrão = `titulo`. */
  chave?: string
  /** Filtro da coluna (opt-in). */
  filtro?: FiltroColuna<T>
}

export interface TabelaAcaoProps<T> {
  colunas: ColunaTabelaAcao<T>[]
  linhas: T[]
  /** Disparado pelo botão da última coluna. */
  onAbrir: (linha: T) => void
  /**
   * Rótulo do botão de ação por linha; padrão `Abrir →`. Use para variar
   * ("Renovar", "Emitir →", "Revisar →"), inclusive por linha.
   */
  renderAcao?: (linha: T) => ReactNode
  /** Chave estável da linha; sem ela usa o índice. */
  chaveDaLinha?: (linha: T, indice: number) => string | number
  /**
   * Desabilita o botão da linha: devolva o MOTIVO (mostrado em tooltip) ou `false` para
   * manter habilitado. Linha desabilitada não chama `onAbrir`. Sem a prop, nada muda.
   */
  acaoDesabilitada?: (linha: T) => string | false
  /**
   * Nome acessível do botão por linha (ex.: "Abrir informativo de X") quando o rótulo
   * visível se repete em todas as linhas. `undefined` = o próprio rótulo.
   */
  rotuloAcessivelAcao?: (linha: T) => string | undefined
  'aria-label'?: string
  /**
   * Estado dos filtros de coluna, CONTROLADO (ex.: para sobreviver a um recarregamento da lista
   * que desmonta a tabela). Sem a prop, a tabela guarda o estado sozinha.
   */
  filtros?: FiltrosTabela
  onFiltrosChange?: (filtros: FiltrosTabela) => void
  /** Texto da contagem com filtros; padrão `N de M`. */
  rotuloContagem?: (visiveis: number, total: number) => string
  /** Estado vazio quando há linhas mas nenhuma passa nos filtros (distinto da lista vazia). */
  mensagemVazioFiltrado?: string
}

const chaveDaColuna = <T,>(coluna: ColunaTabelaAcao<T>): string => coluna.chave ?? coluna.titulo

const normalizar = (s: string): string =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
const soDigitos = (s: string): string => s.replace(/\D/g, '')
const ehFaixa = (v: ValorFiltro | undefined): v is FaixaFiltro =>
  v != null && typeof v === 'object' && !Array.isArray(v)
const numeroOuNulo = (s: string | undefined): number | null => {
  if (s == null || s.trim() === '') return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

/** O filtro restringe alguma coisa? (texto vazio, nenhuma escolha e faixa em branco não contam) */
export const filtroAtivo = (valor: ValorFiltro | undefined): boolean => {
  if (valor == null) return false
  if (typeof valor === 'string') return valor.trim() !== ''
  if (Array.isArray(valor)) return valor.length > 0
  return valor.de.trim() !== '' || valor.ate.trim() !== ''
}

/** Opções distintas (por `valor`) presentes nas linhas, ordenadas por grupo e rótulo. */
export const opcoesPresentes = <T,>(linhas: T[], opcoes: (linha: T) => OpcaoFiltro[]): OpcaoFiltro[] => {
  const porValor = new Map<string, OpcaoFiltro>()
  for (const linha of linhas) {
    for (const o of opcoes(linha)) if (!porValor.has(o.valor)) porValor.set(o.valor, o)
  }
  return [...porValor.values()].sort(
    (a, b) =>
      (a.grupo ?? '').localeCompare(b.grupo ?? '', 'pt-BR') || a.rotulo.localeCompare(b.rotulo, 'pt-BR')
  )
}

const casaTexto = (consulta: string, textos: Array<string | null | undefined>): boolean => {
  const q = normalizar(consulta)
  // Só dígitos e pontuação de máscara (CNPJ "11.222.333/0001-81" ou "11222333000181"): casa também pelos dígitos.
  const digitosConsulta = /^[\d.\-/\s]+$/.test(consulta.trim()) ? soDigitos(consulta) : ''
  return textos.some((t) => {
    if (t == null || t === '') return false
    if (normalizar(t).includes(q)) return true
    return digitosConsulta !== '' && soDigitos(t).includes(digitosConsulta)
  })
}

/**
 * Linhas que passam em TODOS os filtros ativos (E entre colunas). Função pura: só seleciona
 * linhas já carregadas. Filtro de coluna ausente em `colunas` é ignorado.
 */
export const filtrarLinhas = <T,>(
  colunas: ColunaTabelaAcao<T>[],
  linhas: T[],
  filtros: FiltrosTabela
): T[] => {
  const testes: Array<(linha: T) => boolean> = []
  for (const coluna of colunas) {
    const filtro = coluna.filtro
    const valor = filtros[chaveDaColuna(coluna)]
    if (!filtro || !filtroAtivo(valor)) continue
    switch (filtro.tipo) {
      case 'texto':
        if (typeof valor === 'string') testes.push((l) => casaTexto(valor, filtro.textos(l)))
        break
      case 'selecao': {
        if (!Array.isArray(valor)) break
        const grupoDe = new Map(opcoesPresentes(linhas, filtro.opcoes).map((o) => [o.valor, o.grupo ?? '']))
        const porGrupo = new Map<string, Set<string>>()
        for (const v of valor) {
          const g = grupoDe.get(v) ?? ''
          if (!porGrupo.has(g)) porGrupo.set(g, new Set())
          porGrupo.get(g)!.add(v)
        }
        testes.push((l) => {
          const daLinha = filtro.opcoes(l).map((o) => o.valor)
          return [...porGrupo.values()].every((escolhidos) => daLinha.some((v) => escolhidos.has(v)))
        })
        break
      }
      case 'faixaNumero': {
        if (!ehFaixa(valor)) break
        const min = numeroOuNulo(valor.de)
        const max = numeroOuNulo(valor.ate)
        if (min == null && max == null) break
        testes.push((l) => {
          const v = filtro.valor(l)
          if (v == null) return false
          return (min == null || v >= min) && (max == null || v <= max)
        })
        break
      }
      case 'faixaData': {
        if (!ehFaixa(valor)) break
        const de = valor.de.trim()
        const ate = valor.ate.trim()
        testes.push((l) => {
          const v = filtro.valor(l)
          if (!v) return false
          const dia = v.slice(0, 10)
          return (de === '' || dia >= de) && (ate === '' || dia <= ate)
        })
        break
      }
    }
  }
  if (testes.length === 0) return linhas
  return linhas.filter((l) => testes.every((t) => t(l)))
}

/** Botão da última coluna; desabilitado, explica o motivo num tooltip. */
const BotaoAcao = ({
  rotulo,
  motivoDesabilitado,
  rotuloAcessivel,
  onClick,
}: {
  rotulo: ReactNode
  motivoDesabilitado: string | false
  rotuloAcessivel?: string
  onClick: () => void
}) => {
  const desabilitado = motivoDesabilitado !== false
  const botao = (
    <Button
      size="small"
      color="primary"
      disabled={desabilitado}
      aria-label={rotuloAcessivel}
      onClick={desabilitado ? undefined : onClick}
      sx={{ whiteSpace: 'nowrap', fontWeight: desabilitado ? undefined : 600 }}
    >
      {rotulo}
    </Button>
  )
  if (!desabilitado) return botao
  // Botão desabilitado não recebe eventos de mouse: o tooltip fica no <span> em volta.
  return (
    <Tooltip title={motivoDesabilitado} describeChild>
      <span>{botao}</span>
    </Tooltip>
  )
}

/** Seleção múltipla rotulada (teclado: setas + Enter no menu; Backspace remove a última escolha). */
const FiltroSelecao = ({
  titulo,
  opcoes,
  escolhidos,
  onChange,
}: {
  titulo: string
  opcoes: OpcaoFiltro[]
  escolhidos: string[]
  onChange: (valores: string[]) => void
}) => {
  const porValor = new Map(opcoes.map((o) => [o.valor, o]))
  // Escolha que sumiu das linhas (após recarga) continua visível para poder ser removida.
  const valor = escolhidos.map((v) => porValor.get(v) ?? { valor: v, rotulo: v })
  const agrupa = opcoes.some((o) => o.grupo)
  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      limitTags={2}
      options={opcoes}
      value={valor}
      onChange={(_, v) => onChange(v.map((o) => o.valor))}
      getOptionLabel={(o) => o.rotulo}
      isOptionEqualToValue={(a, b) => a.valor === b.valor}
      groupBy={agrupa ? (o) => o.grupo ?? '' : undefined}
      renderOption={(props, o) => (
        <li {...props} key={o.valor}>
          {o.rotulo}
        </li>
      )}
      noOptionsText="Nenhuma opção"
      renderInput={(params) => <TextField {...params} label={titulo} />}
      sx={{ minWidth: 200 }}
    />
  )
}

/** Barra de filtros: um controle rotulado por coluna filtrável + contagem + "Limpar filtros". */
const BarraFiltros = <T,>({
  colunas,
  linhas,
  filtros,
  alterar,
  limpar,
  visiveis,
  rotuloContagem,
  ariaLabel,
}: {
  colunas: ColunaTabelaAcao<T>[]
  linhas: T[]
  filtros: FiltrosTabela
  alterar: (chave: string, valor: ValorFiltro) => void
  limpar: () => void
  visiveis: number
  rotuloContagem: (visiveis: number, total: number) => string
  ariaLabel: string
}) => {
  const algumAtivo = colunas.some((c) => c.filtro && filtroAtivo(filtros[chaveDaColuna(c)]))
  return (
    <Box
      role="search"
      aria-label={`Filtros: ${ariaLabel}`}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 1.5 }}
    >
      {colunas.map((coluna) => {
        const filtro = coluna.filtro
        if (!filtro) return null
        const chave = chaveDaColuna(coluna)
        const valor = filtros[chave]
        if (filtro.tipo === 'texto') {
          return (
            <TextField
              key={chave}
              size="small"
              label={coluna.titulo}
              placeholder={filtro.placeholder ?? 'Contém…'}
              value={typeof valor === 'string' ? valor : ''}
              onChange={(e) => alterar(chave, e.target.value)}
              sx={{ minWidth: 220 }}
            />
          )
        }
        if (filtro.tipo === 'selecao') {
          return (
            <FiltroSelecao
              key={chave}
              titulo={coluna.titulo}
              opcoes={opcoesPresentes(linhas, filtro.opcoes)}
              escolhidos={Array.isArray(valor) ? valor : []}
              onChange={(v) => alterar(chave, v)}
            />
          )
        }
        const faixa: FaixaFiltro = ehFaixa(valor) ? valor : { de: '', ate: '' }
        const data = filtro.tipo === 'faixaData'
        return (
          <Box key={chave} role="group" aria-label={`Faixa de ${coluna.titulo}`} sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type={data ? 'date' : 'number'}
              label={data ? `${coluna.titulo} de` : `${coluna.titulo} mínimo`}
              value={faixa.de}
              onChange={(e) => alterar(chave, { ...faixa, de: e.target.value })}
              InputLabelProps={data ? { shrink: true } : undefined}
              sx={{ width: data ? 170 : 150 }}
            />
            <TextField
              size="small"
              type={data ? 'date' : 'number'}
              label={data ? `${coluna.titulo} até` : `${coluna.titulo} máximo`}
              value={faixa.ate}
              onChange={(e) => alterar(chave, { ...faixa, ate: e.target.value })}
              InputLabelProps={data ? { shrink: true } : undefined}
              sx={{ width: data ? 170 : 150 }}
            />
          </Box>
        )
      })}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
        <Typography variant="body2" color="text.secondary" role="status" aria-live="polite">
          {rotuloContagem(visiveis, linhas.length)}
        </Typography>
        <Button size="small" onClick={limpar} disabled={!algumAtivo}>
          Limpar filtros
        </Button>
      </Box>
    </Box>
  )
}

/**
 * Tabela padrão do Figma: cabeçalho uppercase (estilo vem do tema) e linhas que
 * terminam em `Abrir →`. Genérica de propósito — as páginas fornecem colunas
 * como `render` e este componente não conhece domínio nenhum. Filtros de coluna são opt-in
 * (`ColunaTabelaAcao.filtro`); sem nenhuma coluna filtrável, nada muda (sem barra, todas as linhas).
 */
export const TabelaAcao = <T,>({
  colunas,
  linhas,
  onAbrir,
  renderAcao,
  chaveDaLinha,
  acaoDesabilitada,
  rotuloAcessivelAcao,
  'aria-label': ariaLabel,
  filtros: filtrosControlados,
  onFiltrosChange,
  rotuloContagem = (n, m) => `${n} de ${m}`,
  mensagemVazioFiltrado = 'Nenhum registro com esses filtros',
}: TabelaAcaoProps<T>) => {
  const [filtrosInternos, setFiltrosInternos] = useState<FiltrosTabela>({})
  const filtros = filtrosControlados ?? filtrosInternos
  const temFiltros = colunas.some((c) => c.filtro)
  const visiveis = useMemo(
    () => (temFiltros ? filtrarLinhas(colunas, linhas, filtros) : linhas),
    [temFiltros, colunas, linhas, filtros]
  )

  const definir = (novos: FiltrosTabela) => {
    if (filtrosControlados === undefined) setFiltrosInternos(novos)
    onFiltrosChange?.(novos)
  }
  const alterar = (chave: string, valor: ValorFiltro) => definir({ ...filtros, [chave]: valor })
  const limpar = () => definir({})

  const nome = ariaLabel ?? 'tabela'
  const vazioFiltrado = temFiltros && linhas.length > 0 && visiveis.length === 0

  return (
    <>
      {temFiltros && (
        <BarraFiltros
          colunas={colunas}
          linhas={linhas}
          filtros={filtros}
          alterar={alterar}
          limpar={limpar}
          visiveis={visiveis.length}
          rotuloContagem={rotuloContagem}
          ariaLabel={nome}
        />
      )}
      {vazioFiltrado ? (
        <Paper elevation={0}>
          <EstadoDeLista
            estado="vazio"
            mensagem={mensagemVazioFiltrado}
            detalhe="Ajuste ou limpe os filtros para ver as demais linhas."
            minHeight={160}
          />
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table aria-label={nome} size="small">
            <TableHead>
              <TableRow>
                {colunas.map((coluna) => (
                  <TableCell key={coluna.titulo} align={coluna.align} sx={{ width: coluna.largura }}>
                    {coluna.titulo}
                  </TableCell>
                ))}
                {/* última coluna: ação por linha */}
                <TableCell align="right" sx={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {visiveis.map((linha, indice) => (
                <TableRow key={chaveDaLinha ? chaveDaLinha(linha, indice) : indice} hover>
                  {colunas.map((coluna) => (
                    <TableCell key={coluna.titulo} align={coluna.align}>
                      {coluna.render(linha)}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <BotaoAcao
                      rotulo={renderAcao ? renderAcao(linha) : 'Abrir →'}
                      motivoDesabilitado={acaoDesabilitada ? acaoDesabilitada(linha) : false}
                      rotuloAcessivel={rotuloAcessivelAcao?.(linha)}
                      onClick={() => onAbrir(linha)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

export default TabelaAcao
